import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import Modal from 'components/Modal';
import { Avatar, Input, message } from 'antd';
import styled from 'styled-components';
import IcSearch from 'components/Icon/icons/IcSearch';
import useGetAllEmployees from '../../services/hooks/useGetAllEmployees';
import Text from 'components/Text';
import { Col } from 'components/Box/Col';
import { Row } from 'components/Box/Row';
import { formatPhone } from 'utils/formatPhone';
import select_box from 'components/Icon/images/select-box.svg';
import unselect_box from 'components/Icon/images/unselect-box.svg';
import { AxiosResponse } from 'axios';
import requestSupportApis from 'services/requestSupportApis';
import { getMessageError } from 'utils/response';

export interface ChangeAssignModalRef {
  open: (assignId: string, ticketId: string) => void;
}

interface Props {
  refetch?: () => void;
}
const ChangeAssignModal = forwardRef<ChangeAssignModalRef, Props>(({ refetch }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [assignId, setAssignId] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { data } = useGetAllEmployees();

  useImperativeHandle(ref, () => ({
    open: (assignId: string, ticketId: string) => {
      setAssignId(assignId);
      setTicketId(ticketId);
      setIsOpen(true);
    },
  }));

  const handleClose = () => {
    setIsOpen(false);
    setAssignId(null);
    setTicketId(null);
  };

  const handleSelectEmployee = (employeeId: string) => {
    setAssignId(employeeId);
  };

  const handleSubmit = async () => {
    try {
      const res: AxiosResponse = await requestSupportApis.updateAssignEmployee(ticketId || '', assignId || '');

      if (res?.data?.data) {
        handleClose();
        message.success('Update assign employee successfully');
        refetch?.();
      }
    } catch (error) {
      message.error(getMessageError(error) || 'Update assign employee failed');
    }
  };

  const filteredEmployees = useMemo(() => {
    return data?.filter((employee) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (employee.name && employee.name.toLowerCase().includes(searchLower)) ||
        (employee.phone && employee.phone.toLowerCase().includes(searchLower))
      );
    });
  }, [data, searchTerm]);

  return (
    <Modal
      title='Change Assign'
      open={isOpen}
      onCancel={handleClose}
      onOk={handleSubmit}
      doneButtonTitle='OK'
      cancelButtonTitle='Close'
      closable={false}
    >
      <HeaderSearch>
        <Input
          placeholder='Search name/phone'
          prefix={<IcSearch />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </HeaderSearch>

      <Content>
        {filteredEmployees?.map((employee) => (
          <StaffItem
            key={employee.id}
            className={employee.id === assignId ? 'active' : ''}
            onClick={() => handleSelectEmployee(employee.id)}
          >
            <Row>
              <Avatar src={''} alt={employee.name}>
                {employee.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Col style={{ gap: 2 }}>
                <Text variant='CONTENT_2'>{employee.name}</Text>
                <Text variant='BODY_2' color='text_4'>
                  {formatPhone(employee.phone)}
                </Text>
              </Col>
            </Row>
            <img src={employee.id === assignId ? select_box : unselect_box} alt='select' />
          </StaffItem>
        ))}
      </Content>
    </Modal>
  );
});

export default ChangeAssignModal;

const HeaderSearch = styled.div`
  width: 100%;
  padding: 16px 24px;
  box-sizing: border-box;
  background: #f6f7fc;

  .ant-input-affix-wrapper {
    border-radius: 4px;
    border: 1px solid #dadada;
    height: 48px;
    font-size: 14px;
  }
`;

const Content = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StaffItem = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    border-radius: 8px;
    background: #f6f7fc;
  }

  &.active {
    border-radius: 8px;
    border: 1px solid rgba(0, 61, 155, 0.2);
    background: #f4f7ff;
  }
`;
